import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { appConfigApiRef } from '../api/appconfig-api';
import { AppConfigProfile } from '../types/types';

const AWS_ACCOUNT_ANNOTATION = 'amazonaws.com/account-id';

const EMPTY_PROFILES: AppConfigProfile[] = [];

type DisasterRecovery = {
  primary_region?: string;
  secondary_region?: string;
};

export function useAppConfigProfiles() {
  const { entity } = useEntity();
  const appConfigApi = useApi(appConfigApiRef);

  const accountId = entity.metadata.annotations?.[AWS_ACCOUNT_ANNOTATION] ?? '';
  const features = entity.spec?.features as
    | { disaster_recovery?: DisasterRecovery }
    | undefined;
  const primaryRegion = features?.disaster_recovery?.primary_region;
  const secondaryRegion = features?.disaster_recovery?.secondary_region;

  const {
    value: profiles,
    loading,
    error,
  } = useAsync(async (): Promise<AppConfigProfile[]> => {
    if (!accountId) return [];

    const regions = [
      ...new Set([
        ...(primaryRegion ? [primaryRegion] : []),
        ...(secondaryRegion ? [secondaryRegion] : []),
      ]),
    ];

    if (regions.length === 0) {
      // No regions configured — fall back to default (no region param)
      return appConfigApi.getProfiles(accountId);
    }

    const results = await Promise.all(
      regions.map(region =>
        appConfigApi
          .getProfiles(accountId, region)
          .catch(() => [] as AppConfigProfile[]),
      ),
    );

    // Deduplicate profiles by id+region
    const seen = new Set<string>();
    return results.flat().filter(p => {
      const key = `${p.id}:${p.region ?? ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [accountId, primaryRegion, secondaryRegion]);

  return { profiles: profiles ?? EMPTY_PROFILES, loading, error };
}
